/*
 * Copyright (c) 2022-2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Injectable} from '@angular/core';
import {User} from './model/user';
import {AuthenticationService} from '../core/authentication/authentication.service';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {EnvironmentService} from '../core/environment.service';

@Injectable({
    providedIn: 'root'
})
export class UserService {

    public user: User;

    constructor(private readonly authenticationService: AuthenticationService,
                private http: HttpClient,
                private readonly environmentService: EnvironmentService) {
        // We have to bind the createUser method's 'this' object to UserService class,
        // otherwise we get an error from AuthenticationModule. 'this' becomes undefined for some reason.
        this.createUser = this.createUser.bind(this);
    }

    /**
     * Step 3:
     * Create an instance of User
     * Returns a promise that is resolved with the current user if details were successfully pulled from
     * the backend.
     *
     */
    public createUser(): Promise<User> {
        return new Promise<User>((resolve, reject) => {
            // pull user object with default OAuth claims.
            const user = this.authenticationService.userInfo;

            if (!user) {
                // Reject the Promise if user is not available
                reject('User information is not available.');
                return;
            }

            this.user = user;
            this.user.roles ??= [];

            this.loadDetails().subscribe(
                (details) => {
                    // 200 OK -- Details were loaded.
                    // we have to be authenticated to be able to hit the userDetails endpoint.
                    resolve(this.user);
                },
                (error) => {
                    // an error here means that esci is down or rejected the request or the url is wrong
                    console.error('Error loading user details:', error);
                    resolve(this.user);
                }
            );
        });
    }


    /**
     * Makes an HTTP call to the backend to retrieve the user's details.
     * User is passed through the JWT token inserted by the HTTP interceptor.
     * To get an OK response from this, we need to have a valid JWT token to authenticate with the
     * backend in the first place.
     *
     * This then assigns the user details from the backend response to the current user object.
     * @private
     */
    private loadDetails(): Observable<any> {
        return this.http.get(this.environmentService.environment.apiUrl + '/esciUI/userDetails')
            .pipe(map((res: any) => {
                // this.user.permissions = res.data[0].permissions;
                this.user.roles = res.roles;
                // this.user.userGroups = res.data[0].userGroups;
            }));
    }

    /**
     * Calls the auth service's Logout() function.
     * Easier to get a user object in scope than the entire auth service.
     */
    public logout() {
        this.authenticationService.logout();
    }

    public enableAutomaticSilentRenew() {
        this.authenticationService.enableSilentRenew();
    }

    public fetchPersistentUserData = (): Observable<Object> => {
        return this.http.get(this.environmentService.environment.apiUrl + '/esciUI/userData',
                             {responseType: 'json'});
    }

    public savePersistentUserData = (userData: any): Observable<ArrayBuffer> => {
        return this.http.post(this.environmentService.environment.apiUrl + '/esciUI/userData',
                              userData, { headers: { 'Content-Type': 'application/json' },
                                          responseType: 'arraybuffer'});
    }

}
